import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export const CALENDAR_MIN_YEAR = 2010;
export const TABLE_PERIOD_MIN_YEAR = 2010;
export const DOB_MIN_YEAR = 1900;

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const MONTH_NAMES_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

function getMonthNames(lang) {
  return lang?.startsWith('it') ? MONTH_NAMES_IT : MONTH_NAMES;
}

function startOfToday() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function clampToRange(date, minDate, maxDate) {
  const d = startOfDay(date);
  if (d < minDate) return new Date(minDate);
  if (d > maxDate) return new Date(maxDate);
  return d;
}

export function parseLocalDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (value == null || value === '') return startOfToday();
  const s = String(value);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return startOfToday();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function toLocalYmd(value) {
  const d = parseLocalDate(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function anchorRectFromEvent(e) {
  const el = e?.currentTarget;
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    bottom: r.bottom,
    right: r.right,
    width: r.width,
    height: r.height,
  };
}

/**
 * Shared calendar: Day → Month → Year. No future dates.
 * Default years: 2010 through today (tables). Pass minYear={1900} for DOB.
 * Pass minDate (start date) so the end date cannot be earlier than start.
 */
export default function AppDatePicker({
  open = false,
  activeDateField = 'start',
  value,
  minYear = CALENDAR_MIN_YEAR,
  minDate,
  maxDate,
  title,
  overlayZIndex,
  anchorRect = null,
  buyColor = '#21C46D',
  sellColor = '#E13232',
  onCancel,
  onConfirm,
}) {
  const { t, i18n } = useTranslation();
  const monthNames = useMemo(() => getMonthNames(i18n.language), [i18n.language]);
  const cap = useMemo(
    () => (maxDate ? parseLocalDate(maxDate) : startOfToday()),
    [maxDate ? toLocalYmd(maxDate) : 'today'],
  );
  const floor = useMemo(() => {
    const fromYear = new Date(minYear, 0, 1);
    const fromProp = minDate ? parseLocalDate(minDate) : fromYear;
    return fromProp > fromYear ? fromProp : fromYear;
  }, [minYear, minDate ? toLocalYmd(minDate) : '']);

  const containerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });
  const [year, setYear] = useState(() => String(startOfToday().getFullYear()));
  const [month, setMonth] = useState(() => String(startOfToday().getMonth() + 1));
  const [day, setDay] = useState(() => String(startOfToday().getDate()));
  const wasOpen = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!justOpened) return;
    const raw = value == null || value === '' ? startOfToday() : parseLocalDate(value);
    const next = clampToRange(raw, floor, cap);
    setYear(String(next.getFullYear()));
    setMonth(String(next.getMonth() + 1));
    setDay(String(next.getDate()));
  }, [open, value, floor, cap]);

  const years = useMemo(() => {
    const list = [];
    for (let y = cap.getFullYear(); y >= floor.getFullYear(); y -= 1) list.push(y);
    return list;
  }, [cap, floor]);

  const months = useMemo(() => {
    const y = Number(year);
    const minM = y === floor.getFullYear() ? floor.getMonth() + 1 : 1;
    const maxM = y === cap.getFullYear() ? cap.getMonth() + 1 : 12;
    return monthNames.map((name, i) => ({
      name,
      value: String(i + 1),
    })).filter((m) => {
      const n = Number(m.value);
      return n >= minM && n <= maxM;
    });
  }, [year, cap, floor, monthNames]);

  const days = useMemo(() => {
    const y = Number(year);
    const m = Number(month);
    const dim = new Date(y, m, 0).getDate();
    const minD = (y === floor.getFullYear() && m === floor.getMonth() + 1) ? floor.getDate() : 1;
    const maxD = (y === cap.getFullYear() && m === cap.getMonth() + 1) ? cap.getDate() : dim;
    const start = Math.min(minD, maxD);
    const end = Math.max(minD, maxD);
    const list = [];
    for (let d = start; d <= Math.min(dim, end); d += 1) list.push(d);
    return list;
  }, [year, month, cap, floor]);

  useEffect(() => {
    if (!open) return;
    if (!months.some((m) => m.value === month)) {
      setMonth(months[0]?.value || '1');
    }
  }, [open, months, month]);

  useEffect(() => {
    if (!open) return;
    if (!days.includes(Number(day))) setDay(String(days[0] || 1));
  }, [open, days, day]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = containerRef.current;
    if (!el) return;
    const gap = 6;
    const h = el.offsetHeight || 200;
    const w = el.offsetWidth || 300;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top;
    let left;
    if (anchorRect) {
      const spaceAbove = anchorRect.top - 8;
      const spaceBelow = vh - anchorRect.bottom - 8;
      if (spaceAbove >= h + gap || spaceAbove >= spaceBelow) {
        top = anchorRect.top - h - gap;
      } else {
        top = anchorRect.bottom + gap;
      }
      left = anchorRect.left;
      if (left + w > vw - 8) left = anchorRect.right - w;
      if (left < 8) left = 8;
      if (left + w > vw - 8) left = Math.max(8, vw - w - 8);
      if (top < 8) top = 8;
      if (top + h > vh - 8) {
        const flipped = anchorRect.bottom + gap;
        if (flipped + h <= vh - 8) top = flipped;
        else top = Math.max(8, Math.min(top, vh - h - 8));
      }
    } else {
      top = Math.max(8, (vh - h) / 2);
      left = Math.max(8, (vw - w) / 2);
    }
    setPos({ top, left, ready: true });
  }, [open, anchorRect, year, month, day]);

  if (!open) return null;

  const confirm = () => {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (!y || !m || !d) return;
    const isEnd = activeDateField === 'end';
    const date = clampToRange(new Date(y, m - 1, d), floor, cap);
    if (isEnd) date.setHours(23, 59, 59, 0);
    else date.setHours(0, 0, 0, 0);
    onConfirm?.(date);
  };

  return createPortal(
    <div
      className={`date-picker-overlay${anchorRect ? ' date-picker-overlay--anchored' : ''}`}
      style={{
        '--buy-color': buyColor,
        '--sell-color': sellColor,
        ...(overlayZIndex != null ? { zIndex: overlayZIndex } : {}),
      }}
      onClick={onCancel}
    >
      <div
        ref={containerRef}
        className={`date-picker-container${anchorRect ? ' date-picker-container--anchored' : ''}`}
        style={
          anchorRect
            ? { top: pos.top, left: pos.left, visibility: pos.ready ? 'visible' : 'hidden' }
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="date-picker-header">
          <button type="button" className="date-picker-btn-cancel" onClick={onCancel}>{t('common.cancel')}</button>
          <span>{title || (activeDateField === 'end' ? t('datePicker.selectEndDate') : t('datePicker.selectStartDate'))}</span>
          <button type="button" className="date-picker-btn-confirm" onClick={confirm}>{t('datePicker.confirm')}</button>
        </div>
        <div className="date-picker-wheels">
          <div className="picker-column app-scrollbar">
            {days.map((d) => (
              <div
                key={d}
                className={`picker-item ${day === String(d) ? 'selected' : ''}`}
                onClick={() => setDay(String(d))}
              >
                {d}
              </div>
            ))}
          </div>
          <div className="picker-column app-scrollbar">
            {months.map((m) => (
              <div
                key={m.value}
                className={`picker-item ${month === m.value ? 'selected' : ''}`}
                onClick={() => setMonth(m.value)}
              >
                {m.name}
              </div>
            ))}
          </div>
          <div className="picker-column app-scrollbar">
            {years.map((y) => (
              <div
                key={y}
                className={`picker-item ${year === String(y) ? 'selected' : ''}`}
                onClick={() => setYear(String(y))}
              >
                {y}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
